import { useQuery, useQueryClient } from "@tanstack/react-query";
import { custom, z } from "zod";
import { Decimal } from "decimal.js";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
  MimeType,
} from "../../utils";


export const CsvUploadReportValidation = z.object({
  filename: z.string(),
  mime_type: z.enum([MimeType.Csv]),
})

export interface IngestRjoWorkingOrderJobResult {
  link: string;
  document_id: string;
  job_id: string;
}

export const ActionOpenOrdersValidation = z.object({
  allow_ids: z.array(z.string().uuid()),
  delete_ids: z.array(z.string().uuid()),
});

export const CreateBrokerageStagedReviewSessionValidation = z.object({
  trader_id: z.string().uuid(),
  provider_type: z.enum(["RJO"]),
  trading_order_type: z.enum(["open_position", "p_and_s"]),
  accepted_hedgeable_row_ids: z.array(z.string().uuid()),
  accepted_non_hedgeable_row_ids: z.array(z.string().uuid()),
  dismissed_row_ids: z.array(z.string().uuid()),
});

export const ListBrokerageStagedPositionsForValidation = z.object({
  trader_id: z.string().uuid(),
});

export interface OpenDiffResponse {
  accepted: BrokerageAccountPosition[];
  staged_report: BrokerageAccountPositionSuggestionReport
}

export interface BrokerageAccountPositionSuggestionReport {
    add: BrokerageAccountPosition[];
    skip: BrokerageAccountPosition[];
    unknown: BrokerageAccountPosition[];
}

export const UNKNOWN_TRADER_ID = "Unknown";
export type BrokerageAccountPositionStagingStatus = "staged" | "accepted" | "dismissed";
export type StatusCategory = "hedgeable" | "non_hedgeable" | "flagged";
export interface BrokerageAccountPositionStaging {
    id: string;
    created_at: Date;
    updated_at: Date;

    provider_type: string;
    trading_order_type: string;
    external_id: string;
    position_id: string;

    approval_session_id: string | null;
    trader_id: string | null;
    import_date: Date;

    status: BrokerageAccountPositionStagingStatus;
    status_category: StatusCategory | null;

    brokerage_account_data: BrokerageAccountPosition;

    trader?: { name: string; id: string };
}

export interface DiffBreakdownResult {
  suggested_accept_hedgeable: BrokerageAccountPositionStaging[];
  suggested_accept_non_hedgeable: BrokerageAccountPositionStaging[];
  suggested_accept_hedgeable_carryover: BrokerageAccountPositionStaging[];
  suggested_accept_non_hedgeable_carryover: BrokerageAccountPositionStaging[];
  /** Backwards compatible - unused. Always empty array from the backend. */
  suggested_accept_carryover: BrokerageAccountPositionStaging[];
  /** Backwards compatible - unused. Always empty array from the backend. */
  suggested_accept: BrokerageAccountPositionStaging[];
  suggested_flagged: BrokerageAccountPositionStaging[];
  suggested_dismiss: BrokerageAccountPositionStaging[];
  suggested_non_applicable: BrokerageAccountPositionStaging[];
  removed: BrokerageAccountPositionStaging[];
}

export interface BrokerageStagedPositionsDiffResult {
  current_positions_by_trader: Record<string, BrokerageAccountPosition[]>;
  last_session: Record<string, BrokerageAccountPositionStaging[]>;
  suggested_report: Record<string, DiffBreakdownResult>;
}

export interface PAndSDiffBreakdownResult {
  suggested_accept_hedgeable: BrokerageAccountPositionStaging[];
  suggested_accept_non_hedgeable: BrokerageAccountPositionStaging[];
  suggested_flagged: BrokerageAccountPositionStaging[];
  suggested_non_applicable: BrokerageAccountPositionStaging[];
}

export interface PAndSStagedPositionsDiffResult {
  suggested_report: Record<string, PAndSDiffBreakdownResult>;
}

export interface BrokerageAccountPosition {
  id: string;
  created_at: Date;
  updated_at: Date;

  is_manually_inserted: boolean;
  is_staged: boolean;
  is_hedgeable?: boolean;

  instrument_type: string | undefined | null;
  external_id: string | undefined | null;

  provider_type: "RJO";
  trading_order_type: "working" | "consolidated" | "open_position" | "p_and_s";
  trading_order_subtype?: "p_and_s" | "p_and_s_reversal" | null;
  order_status: "working" | "confirmed";

  trading_account_id: string | undefined | null;
  customer_id: string | undefined | null;
  trader_id: string | undefined | null;

  trader_name: string | undefined | null;
  customer_name: string | undefined | null;
  trading_account: string;

  deleted_at: Date | undefined | null;
  placed_date: Date | undefined | null;
  order_date: Date[];
  removed_at: Date | undefined | null;

  long_qty: number;
  short_qty: number;

  note: string | undefined | null;

  option_month: string;
  symbol_code: string;

  commodity_type: string | undefined | null;
  commodity_label: string | undefined | null;

  crop_year: number | undefined | null;
  put_or_call: string | undefined | null;

  position_type?: "Future" | "Option" | null;

  contract_description: string;

  price?: string;
  strike_price?: string | null;

  uom_quantity?: string | null;
  limit_price?: string |  Decimal | null;


  original_realized_amount?: Decimal | null;
  currency_adjusted_realized_amount?: Decimal | null;

  trade_price?: string | Decimal | null;
  close_price?: string | Decimal | null;

  original_currency_type_code?: string | null;
  original_currency_code?: string | null;

  Customer?: { id: string; long_name: string; };
}




export function useGenerateRjoWorkingOrderUpload() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CsvUploadReportValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<IngestRjoWorkingOrderJobResult>(
      `risk/brokerage/workingOrders/rjo/upload`,
      data,
    );

    return result;
  };
}


export function useActionOnStagedOpenOrders() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof ActionOpenOrdersValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<IngestRjoWorkingOrderJobResult>(
      `risk/brokerage/open/staged/action`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "brokerage"] });
    }

    return result;
  };
}

export function useListOpenOrdersDiff() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["risk", "brokerage", "diff"],
    queryFn: () =>
      fetchApiGet<OpenDiffResponse>("risk/brokerage/open/diff", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}


export function useActionOnStagedSessionPositions() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateBrokerageStagedReviewSessionValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ approval_session_id: string }>(
      `risk/brokerage/open/staged/session/action`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "brokerage"] });
    }

    return result;
  };
}

export function useListStagedSessionPositions() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["risk", "brokerage", "staged", "diff"],
    queryFn: () =>
      fetchApiGet<BrokerageStagedPositionsDiffResult>("risk/brokerage/open/staged/diff", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useListStagedSessionPositionsForPAndS() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["risk", "brokerage", "p_and_s", "staged", "diff"],
    queryFn: () =>
      fetchApiGet<PAndSStagedPositionsDiffResult>("risk/brokerage/p_and_s/staged/diff", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useActionOnStagedSessionPositionsForPAndS() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateBrokerageStagedReviewSessionValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{ approval_session_id: string }>(
      `risk/brokerage/p_and_s/staged/session/action`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk", "brokerage"] });
    }

    return result;
  };
}




